import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { BakingProductionLog, BakingProductionLogState } from '../../types/bakingProductionLog';
import bakingProductionLogApiService from '../../services/bakingProductionLogApiService';

// Helper function to create a blank baking production log with default values
export const createBlankBakingProductionLog = (): BakingProductionLog => {
  const now = new Date();

  // Format date in local timezone as YYYY-MM-DDTHH:MM for datetime-local input
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const currentTimestamp = `${year}-${month}-${day}T${hours}:${minutes}`;

  return {
    id: uuidv4(),
    fillingId: '',
    fillingName: '',
    lotNumber: '',
    startTime: currentTimestamp,
    finishTime: '', // Empty - user enters later
    ovenTemp: 350, // Default to 350°F
    staffInitials: '', // Optional
    correctiveAction: '',
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  };
};

// Async Thunks

export const fetchBakingProductionLogs = createAsyncThunk(
  'bakingProductionLog/fetchLogs',
  async () => {
    return await bakingProductionLogApiService.fetchBakingProductionLogs();
  }
);

export const createBakingProductionLogAsync = createAsyncThunk(
  'bakingProductionLog/createLog',
  async (log: BakingProductionLog) => {
    const response = await bakingProductionLogApiService.createBakingProductionLog(log);
    // Return the full response so component can access batchRecordId via unwrap()
    // The fulfilled handler will extract only the log
    return response;
  }
);

export const updateBakingProductionLogAsync = createAsyncThunk(
  'bakingProductionLog/updateLog',
  async (log: BakingProductionLog) => {
    return await bakingProductionLogApiService.updateBakingProductionLog(log);
  }
);

export const deleteBakingProductionLogAsync = createAsyncThunk(
  'bakingProductionLog/deleteLog',
  async (id: string) => {
    const result = await bakingProductionLogApiService.deleteBakingProductionLog(id);
    return {
      id,
      batchRecordId: result.batchRecordId,
      traceCodeId: result.traceCodeId,
    };
  }
);

// Initial State
const initialState: BakingProductionLogState = {
  logs: [],
  loading: false,
  error: null,
};

// Slice
const bakingProductionLogSlice = createSlice({
  name: 'bakingProductionLog',
  initialState,
  reducers: {
    // Synchronous reducers
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Baking Production Logs
    builder
      .addCase(fetchBakingProductionLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBakingProductionLogs.fulfilled, (state, action: PayloadAction<BakingProductionLog[]>) => {
        state.loading = false;
        state.logs = action.payload;
      })
      .addCase(fetchBakingProductionLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch baking production logs';
      });

    // Create Baking Production Log
    builder
      .addCase(createBakingProductionLogAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBakingProductionLogAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Extract only the log from the response
        state.logs.push(action.payload.log);
      })
      .addCase(createBakingProductionLogAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create baking production log';
      });

    // Update Baking Production Log
    builder
      .addCase(updateBakingProductionLogAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBakingProductionLogAsync.fulfilled, (state, action: PayloadAction<BakingProductionLog>) => {
        state.loading = false;
        const index = state.logs.findIndex((log) => log.id === action.payload.id);
        if (index !== -1) {
          state.logs[index] = action.payload;
        }
      })
      .addCase(updateBakingProductionLogAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update baking production log';
      });

    // Delete Baking Production Log
    builder
      .addCase(deleteBakingProductionLogAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBakingProductionLogAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Extract only the log ID from the payload
        state.logs = state.logs.filter((log) => log.id !== action.payload.id);
      })
      .addCase(deleteBakingProductionLogAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete baking production log';
      });
  },
});

export const { clearError } = bakingProductionLogSlice.actions;
export default bakingProductionLogSlice.reducer;
